// CollectionPickerDialog — searchable-пикер коллекций для масштабирования drag'а.
//
// Открывается, когда карточку бросили на цель «Выбрать коллекцию…» в доке
// (dragSession.pendingPick). Показывает ПОИСК по всем коллекциям — так десятки
// коллекций не нужно ловить мышью в доке: бросил в «Выбрать…», нашёл, кликнул.
// Выбор коллекции маршрутизируется тем же handleDrop (material/essay → addItems;
// bound-артефакт → модал «материал vs копия»). Смонтирован в layout.

import { Button } from "@studyhub/ui/components/button";
import { EmptyState } from "@studyhub/ui/components/empty-state";
import {
	ResponsiveDialog,
	ResponsiveDialogBody,
	ResponsiveDialogContent,
	ResponsiveDialogHeader,
	ResponsiveDialogTitle,
} from "@studyhub/ui/components/responsive-dialog";
import { DIALOG_CONTENT_BASE, SHEET_H_PICKER } from "@studyhub/ui/tokens/sheet-heights";
import { IconFolderFilled, IconSearch, IconStack2 } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMaterialSets } from "@/components/materials/_api";
import { cn } from "@/lib/cn";
import { useDragSessionStore } from "@/stores/drag-session";
import { useCollectionDrop } from "./use-collection-drop";

const DEFAULT_COLOR = "#6366F1";

export function CollectionPickerDialog(): React.ReactElement {
	const { t } = useTranslation("library");
	const item = useDragSessionStore((s) => s.pendingPick);
	const closePick = useDragSessionStore((s) => s.closePick);
	const setsQuery = useMaterialSets();
	const { handleDrop } = useCollectionDrop();
	const [q, setQ] = useState("");

	const all = useMemo(() => setsQuery.data?.items ?? [], [setsQuery.data]);
	const filtered = useMemo(() => {
		const needle = q.trim().toLowerCase();
		const items = needle ? all.filter((s) => s.name.toLowerCase().includes(needle)) : all;
		return items.toSorted((a, b) => b.updatedAt.localeCompare(a.updatedAt));
	}, [all, q]);

	function handleOpenChange(open: boolean): void {
		if (!open) {
			setQ("");
			closePick();
		}
	}

	function pick(setId: string): void {
		if (!item) return;
		handleDrop(item, setId);
		setQ("");
		closePick();
	}

	return (
		<ResponsiveDialog open={item !== null} onOpenChange={handleOpenChange}>
			<ResponsiveDialogContent className={cn(DIALOG_CONTENT_BASE, SHEET_H_PICKER, "sm:max-w-md")}>
				<ResponsiveDialogHeader className="shrink-0 border-b border-border px-4 py-3 text-left">
					<ResponsiveDialogTitle className="text-base">{t("dock.pickTitle")}</ResponsiveDialogTitle>
				</ResponsiveDialogHeader>

				<div className="shrink-0 p-3">
					<label className="flex h-10 items-center gap-2 rounded-[10px] border border-border bg-[var(--well)] px-3 focus-within:border-primary focus-within:ring-2 focus-within:ring-ring/40">
						<IconSearch size={16} stroke={1.75} className="shrink-0 text-muted-foreground" />
						<input
							value={q}
							onChange={(e) => setQ(e.target.value)}
							placeholder={t("dock.pickSearch")}
							aria-label={t("dock.pickSearch")}
							className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
						/>
					</label>
				</div>

				<ResponsiveDialogBody>
					<div className="flex flex-col gap-1 p-3 pt-0">
						{filtered.length === 0 ? (
							<EmptyState
								title={t("dock.pickEmpty")}
								className="border-0 bg-transparent py-8 shadow-none"
							/>
						) : (
							filtered.map((s) => {
								const color = s.color ?? DEFAULT_COLOR;
								return (
									<Button
										key={s.id}
										type="button"
										variant="ghost"
										onClick={() => pick(s.id)}
										className="h-auto w-full cursor-pointer justify-start gap-3 px-2.5 py-2 text-left [&_svg]:size-auto"
									>
										<span
											className="flex size-9 shrink-0 items-center justify-center rounded-[var(--radius-md)]"
											style={{ backgroundColor: `${color}1f`, color }}
										>
											<IconFolderFilled size={18} stroke={1.75} />
										</span>
										<span className="flex min-w-0 flex-1 flex-col">
											<span className="truncate text-sm font-medium text-foreground">{s.name}</span>
											<span className="flex items-center gap-1 text-2xs tabular-nums text-muted-foreground">
												<IconStack2 size={11} stroke={1.75} />
												{s.materialsCount}
											</span>
										</span>
									</Button>
								);
							})
						)}
					</div>
				</ResponsiveDialogBody>
			</ResponsiveDialogContent>
		</ResponsiveDialog>
	);
}
